import { GRADE_SCALE_PRESETS } from '@repo/lib/constants/grade-scale-constants';
import { gradeMappingCodec, type GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';
import { AwardIcon, BanIcon, SlidersHorizontalIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { GradeMappingDialog } from './GradeMappingDialog';

export interface GradeScaleOptionsMenuProps {
  /** The trigger element rendered as the dropdown's anchor. */
  children: ReactNode;
  currentGradeMapping: GradeMappingSchema | null;
  /** Fired for the "remove" item and the built-in presets. */
  onSelectPreset: (gradeMapping: GradeMappingSchema | null, label: string) => void;
  /** Fired when the custom-scale dialog is confirmed. */
  onSelectCustom: (gradeMapping: GradeMappingSchema | null) => void;
  removeLabel?: string;
  removeDisabled?: boolean;
  align?: 'start' | 'center' | 'end';
}

/**
 * Shared scale picker — the built-in presets, the custom-scale dialog, and the
 * "remove grading" item. Selecting a preset or removing reports via
 * onSelectPreset; confirming the custom dialog reports via onSelectCustom, so
 * callers can stage, submit, or just store the mapping as they see fit.
 */
export function GradeScaleOptionsMenu(gradeScaleOptionsMenuProps: GradeScaleOptionsMenuProps) {
  const { children, currentGradeMapping, onSelectPreset, onSelectCustom, removeLabel = 'Usuń ocenianie', removeDisabled = false, align = 'end' } = gradeScaleOptionsMenuProps;
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  return (
    <>
      <GradeMappingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialGradeMapping={currentGradeMapping}
        onConfirm={(gradeMapping) => {
          setDialogOpen(false);
          onSelectCustom(gradeMapping);
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-64">
          <DropdownMenuGroup>
            <DropdownMenuItem disabled={removeDisabled} onSelect={() => onSelectPreset(null, 'bez oceniania')}>
              <BanIcon />
              {removeLabel}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuLabel>Domyślne schematy</DropdownMenuLabel>
            {GRADE_SCALE_PRESETS.map((preset) => (
              <DropdownMenuItem key={preset.name} onSelect={() => onSelectPreset(gradeMappingCodec.decode(preset.bands), preset.name)}>
                <AwardIcon />
                {preset.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => setDialogOpen(true)}>
              <SlidersHorizontalIcon />
              Wybierz i dostosuj skalę…
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
